import { useLocation } from "@tanstack/react-router";
import { useSidebar } from "@/components/ui/sidebar";
import { useTabsState } from "@/contexts/tabs-context";
import { matchWorkspaceLocation } from "@/routing/core/locations";
import { KeepAliveContainer } from "@/routing/keep-alive/KeepAliveContainer";
import { ProjectSidebar } from "./project-sidebar";

/**
 * ProjectSidebarsHost renders per-project sidebars for all open tabs.
 *
 * This mirrors the keep-alive pattern used by ProjectsHost for project content:
 * all sidebars stay mounted via CSS show/hide, preserving tree expansion state,
 * scroll position, and loaded data across tab switches.
 *
 * On mobile, only the active project sidebar is mounted. shadcn Sidebar renders
 * as a portaled Sheet on mobile, which bypasses CSS display:none. Mounting all
 * sidebars would cause multiple Sheets to open simultaneously.
 *
 * On desktop, all sidebars stay mounted (keep-alive) with CSS display toggling.
 */
export function ProjectSidebarsHost() {
  const { tabs, activeTabId } = useTabsState();
  const { isMobile } = useSidebar();
  const location = useLocation();
  const activeWorkspace = matchWorkspaceLocation(location.pathname);
  const isProjectRoute = activeWorkspace?.kind === "project";
  const projectTabs = tabs.filter((tab) => tab.kind === "project");

  if (projectTabs.length === 0) {
    return null;
  }

  return (
    <>
      {projectTabs.map((tab) => {
        const isVisible = isProjectRoute && tab.tabId === activeTabId;

        if (isMobile && !isVisible) {
          return null;
        }

        return (
          <KeepAliveContainer
            key={tab.tabId}
            active={isVisible}
            variant="contents"
            data-project-sidebar={tab.tabId}
          >
            <ProjectSidebar
              tabId={tab.tabId}
              projectId={tab.resourceId}
              currentPath={tab.currentPath}
              searchParams={tab.searchParams}
              isActive={isVisible}
            />
          </KeepAliveContainer>
        );
      })}
    </>
  );
}
